//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useEffect, useContext, useRef } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';

// === COMPONENTS ===
import { Spacer } from 'components/Spacer';

// === UTILS ===
import { keyframes } from 'components/utils';
import { DataTypeSelect } from '../../../../../DataTypeSelect/DataTypeSelect';

//#endregion

//-----------------------------------------------------------------
export function ColumnMap( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { index, id, type, field1, field2, columns1, columns2, onColumnChange, onColumnRemove, onColumnReorder } = props;

  const ref = useRef();

  const [ field1_value  , setField1Value  ] = useState( field1 );
  const [ field2_value  , setField2Value  ] = useState( field2 );

  const [ field1_option , setField1Option ] = useState( columns1?.find( x => x.value === field1 ) );

  const [ is_changed_field1, setIsChangedField1 ] = useState( false );
  const [ is_changed_field2, setIsChangedField2 ] = useState( false );

  const [is_drag      , setIsDrag     ] = useState(false);
  const [is_drop      , setIsDrop     ] = useState(false);
  const [is_drop_above, setIsDropAbove] = useState(false);
  const [drag_data    , setDragData   ] = useState( null); // { id: '1', field1: '', field2: '' }

  //#endregion

  //#region [STYLES]

  const theme = useTheme();
  const _styles = {
    container: {
      // display    : 'flex',
      width        : '100%',
      // alignItems : 'center',
    },
    container2: {
      display      : 'flex',
      width        : '100%',
      alignItems   : 'center',
      mixBlendMode : is_drag ? 'multiply' : 'normal',
      overflow     : 'auto',
    },
    container_placeholder: {
      display    : 'flex',
      alignItems : 'center',
      backgroundColor: theme.palette.primary[theme.palette.mode],
    },
    select_field2: {
      // flexShrink : 0,
      width: '40rem',
      animation: is_changed_field1 ? `${keyframes.confirm} 1s 1` : '',
    },
    text_field2: {
      // flexShrink : 0,
      width: '40rem',
      animation: is_changed_field2 ? `${keyframes.confirm} 1s 1` : '',
    },
    button_remove: {
      width  : '3rem',
      height : '3rem',
    },
  }

  const CONTAINER = {
    style : _styles.container,
    ref   : ref,
    key   : id,
    draggable   : true,
    onDragEnter : onDragEnter,
    onDragLeave : onDragLeave,
    onDragOver  : onDragOver ,
    onDrop      : onDrop     ,
    onDragStart : onDragStart,
    onDragEnd   : onDragEnd  ,
  }

  const CONTAINER2 = {
    style : _styles.container2,
    ref   : ref,
    key   : id,
  }

  const CONTAINER_PLACEHOLDER = {
    style : _styles.container_placeholder,
  }

  const SELECT_TABLE_1_COLUMN = {
    sx               : _styles.select_field2,
    key              : 'f1-'+id,
    options          : columns1,
    value            : field1_option,
    inputValue       : field1_value ,
    size             : 'small',
    freeSolo         : true,
    disableClearable : true,
    onChange         : onChange_Field1_Option,
    onInputChange    : onChange_Field1_Input,
    renderInput      : (params) => <TextField {...params} label="Column" />,
    getOptionLabel   : (option) => option.label || option,
    // children         : columns1?.map( item => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
  };

  const TEXT_TABLE_2_COLUMN = {
    sx          : _styles.text_field2,
    key         : 'f2-'+id,
    value       : field2_value,
    size        : 'small',
    onChange    : onChange_Field2_Value,
    onKeyDown   : onKeyDown_Field2     ,
  };

  const PLACEHOLDER_TABLE_1_COLUMN = {
    sx          : _styles.text_field2,
    key         : 'pf1-'+id,
    value       : drag_data?.field1,
    size        : 'small',
  };

  const PLACEHOLDER_TABLE_2_COLUMN = {
    sx          : _styles.text_field2,
    key         : 'pf2-'+id,
    value       : drag_data?.field2,
    size        : 'small',
  };

  const BUTTON_DRAG = {
    sx: _styles.button_remove,
    key : 'drag',
    children: <DragIndicator />,
  };

  const BUTTON_DELETE = {
    sx: _styles.button_remove,
    key : 'remove',
    onClick: onRemove,
    children: <Delete />,
  };
    
  //#endregion

  //#region [EFFECT]

  useEffect( () => {

  }, [] );

  //#endregion

  //#region [EVENTS]
  
  //-----------------------------------------------------------------
  function onChange_DataType( value ) {
    //-----------------------------------------------------------------
    onColumnChange( id, value, undefined, undefined );
  }
  
  //-----------------------------------------------------------------
  function onChange_Field1_Option( event, value ) {
    //-----------------------------------------------------------------
    setField1Option( value );
    setIsChangedField1( true )
    setTimeout( () => setIsChangedField1(false), 500 )
  }
  
  //-----------------------------------------------------------------
  function onChange_Field1_Input( event, value ) {
    //-----------------------------------------------------------------
    setField1Value( value );
    // const update = is_predicate ? updatePredicate : updateExpression;
    onColumnChange( id, undefined, value, undefined );
    // update( id , { table1: { source: table1_source, name: value } } )
  }
  
  //-----------------------------------------------------------------
  function onChange_Field2_Value( event ) {
    //-----------------------------------------------------------------
    const value = event.target.value;
    setField2Value( value );
    onColumnChange( id, undefined, undefined, field2_value );
  }
  
  //-----------------------------------------------------------------
  function onKeyDown_Field2( event ) {
    //-----------------------------------------------------------------
    if (event.key === 'Enter') {
      onColumnChange( id, undefined, undefined, field2_value );
      setIsChangedField2( true )
      setTimeout( () => setIsChangedField2(false), 500 )
    }
  }
  
  //-----------------------------------------------------------------
  function onRemove( event ) {
    //-----------------------------------------------------------------
    onColumnRemove( id );
  }

  /** === Drag & Drop === */

  //-----------------------------------------------------------------
  function onDragStart( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData( 'data/column', JSON.stringify( {id, index, field1, field2} ) );
    event.target.style.opacity = 1.0;
    setIsDrag( true );
  }

  //-----------------------------------------------------------------
  function onDragEnd( event ) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    setIsDrag( false );
  }

  //-----------------------------------------------------------------
  function onDragEnter( event ) {
    //-----------------------------------------------------------------
    const is_column = event.dataTransfer.types.includes("data/column");
    if( !is_column ) return;

    const column_data_string = event.dataTransfer.getData( "data/column" );
    const column_data        = column_data_string ? JSON.parse( column_data_string ) : {};
    if ( column_data.id === id ) return;

    event.stopPropagation();
    event.preventDefault();
    
    if( !is_drop ) {
      setIsDrop(true);
      setDragData( column_data );
    }
  }

  //-----------------------------------------------------------------
  function onDragLeave( event ) {
    //-----------------------------------------------------------------
    const is_column = event.dataTransfer.types.includes("data/column");
    if( !is_column ) return;

    const column_data_string = event.dataTransfer.getData( "data/column" );
    const column_data        = column_data_string ? JSON.parse( column_data_string ) : {};
    if ( column_data.id === id ) return;

    event.stopPropagation();
    event.preventDefault();

    const rect = ref.current.getBoundingClientRect();
    if(event.clientY < rect.top || event.clientY >= rect.bottom || event.clientX < rect.left || event.clientX >= rect.right) {
      setIsDrop(false);
      setDragData( null );
    }
  }

  //-----------------------------------------------------------------
  function onDragOver( event ) {
    //-----------------------------------------------------------------
    const is_column = event.dataTransfer.types.includes("data/column");
    if( !is_column ) return;

    const column_data_string = event.dataTransfer.getData( "data/column" );
    const column_data        = column_data_string ? JSON.parse( column_data_string ) : {};
    if ( column_data.id === id ) return;
    
    event.stopPropagation();
    event.preventDefault();

    const is_directly_above = index - column_data.index ===  1;
    const is_directly_below = index - column_data.index === -1;

    const bounding = ref.current.getBoundingClientRect()
    const offset = bounding.y + (bounding.height/2);
    const is_mouse_below = event.clientY - offset > 0;
    const is_mouse_above = event.clientY - offset < 0;

    if( is_directly_above && is_mouse_above ) setIsDrop( false );
    if( is_directly_below && is_mouse_below ) setIsDrop( false );

    if( is_mouse_below &&  is_drop_above ) return setIsDropAbove( false );
    if( is_mouse_above && !is_drop_above ) return setIsDropAbove(  true );
  }

  //-----------------------------------------------------------------
  function onDrop( event ) {
    //-----------------------------------------------------------------
    const is_column = event.dataTransfer.types.includes("data/column");
    if ( !is_column ) return;

    const column_data_string = event.dataTransfer.getData( "data/column" );
    const column_data        = column_data_string ? JSON.parse( column_data_string ) : {};
    if ( column_data.id === id ) return;

    event.stopPropagation();
    event.preventDefault();

    setIsDrop(false);
    setDragData( null );
    onColumnReorder( column_data.id, id, is_drop_above );
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <div key='id' { ...CONTAINER }>
        { is_drop &&  is_drop_above ? displayPlaceholder('p1') : <div /> }
        <div { ...CONTAINER2 }>
          <IconButton   { ...BUTTON_DRAG     } />
          <IconButton   { ...BUTTON_DELETE   } />
          <Spacer />
          <DataTypeSelect id={id} type={type} onChange={onChange_DataType} />
          <Spacer />
          <Autocomplete  { ...SELECT_TABLE_1_COLUMN } />
          <Spacer />
          <div style={{fontSize:'30px'}} >{'='}</div>
          <Spacer />
          <TextField { ...TEXT_TABLE_2_COLUMN } />
        </div>
        { is_drop && !is_drop_above ? displayPlaceholder('p2') : <div /> }
      </div>
    )
  }

  //-----------------------------------------------------------------
  function displayPlaceholder(key) {
    //-----------------------------------------------------------------
    return (
      <div key={key} { ...CONTAINER_PLACEHOLDER }>
        <IconButton   { ...BUTTON_DRAG     } />
        <IconButton   { ...BUTTON_DELETE   } />
        <TextField { ...PLACEHOLDER_TABLE_1_COLUMN } />
        <Spacer />
        <div style={{fontSize:'30px'}} >{'='}</div>
        <Spacer />
        <TextField { ...PLACEHOLDER_TABLE_2_COLUMN } />
      </div>
    )
  }

  //#endregion

  return display();
}